import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/axois';
import styles from './PaymentSuccess.module.css';

const fmt = (dateStr) =>
    dateStr
        ? new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
        : '—';

const PaymentSuccess = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [booking, setBooking] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const isGuest = searchParams.get('guest') === 'true';
    const bookingId = searchParams.get('booking_id');
    const affiliateCode = searchParams.get('affiliate_code');

    useEffect(() => {
        const fetchBookingDetails = async () => {
            if (!bookingId) {
                setError('No booking ID provided');
                setLoading(false);
                return;
            }
            try {
                const response = await api.get(`/bookings/${bookingId}`);
                setBooking(response.data.booking);
            } catch (err) {
                setError('Failed to fetch booking details');
            } finally {
                setLoading(false);
            }
        };
        fetchBookingDetails();
    }, [bookingId]);

    if (loading) {
        return (
            <div className={styles.psPage}>
                <div className={styles.psLoading}>Loading your booking…</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.psPage}>
                <div className={styles.psCard}>
                    <p className={styles.psErrorLabel}>Something went wrong</p>
                    <h1 className={styles.psTitle}>{error}</h1>
                    <div className={styles.psActions}>
                        <button onClick={() => navigate('/')} className={styles.psBtnPrimary}>Go to Home</button>
                        {isGuest && (
                            <button onClick={() => navigate('/login')} className={styles.psBtnOutline}>Login to view bookings</button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    const nights = booking
        ? Math.round((new Date(booking.end_date) - new Date(booking.start_date)) / 86400000)
        : 0;

    return (
        <div className={styles.psPage}>
            <div className={styles.psCard}>

                {/* ── Header band ── */}
                <div className={styles.psHeader}>
                    <div className={styles.psHeaderLeft}>
                        <span className={styles.psEyebrow}>Booking Confirmation</span>
                        <h1 className={styles.psBrand}>Aurelia Apartments</h1>
                    </div>
                    {booking && (
                        <div className={styles.psHeaderRight}>
                            <span className={styles.psInvoiceLabel}>Receipt</span>
                            <span className={styles.psInvoiceNum}>#BKNG-{booking.id}</span>
                        </div>
                    )}
                </div>

                {/* ── Status strip ── */}
                <div className={styles.psStatusStrip}>
                    <span className={styles.psStatusDot} />
                    <span className={styles.psStatusText}>Booking Confirmed</span>
                    <span className={styles.psStatusSub}>A receipt has been sent to your email</span>
                </div>

                {/* ── Body ── */}
                <div className={styles.psBody}>

                    {/* Guest info */}
                    {(isGuest || affiliateCode) && booking && (
                        <>
                            <h2 className={styles.psSectionTitle}>Guest Details</h2>
                            <div className={styles.psDetailGrid}>
                                {booking.guest_name && (
                                    <div className={styles.psDetailCell}>
                                        <span className={styles.psDetailLabel}>Guest Name</span>
                                        <span className={styles.psDetailValue}>{booking.guest_name}</span>
                                    </div>
                                )}
                                {booking.guest_email && (
                                    <div className={styles.psDetailCell}>
                                        <span className={styles.psDetailLabel}>Email</span>
                                        <span className={styles.psDetailValue}>{booking.guest_email}</span>
                                    </div>
                                )}
                                {booking.guest_phone && (
                                    <div className={styles.psDetailCell}>
                                        <span className={styles.psDetailLabel}>Phone</span>
                                        <span className={styles.psDetailValue}>{booking.guest_phone}</span>
                                    </div>
                                )}
                                {affiliateCode && (
                                    <div className={styles.psDetailCell}>
                                        <span className={styles.psDetailLabel}>Affiliate Code</span>
                                        <span className={styles.psDetailValue}>{affiliateCode}</span>
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    {/* Reservation details */}
                    {booking && (
                        <>
                            <h2 className={styles.psSectionTitle}>Reservation Details</h2>
                            <div className={styles.psDetailGrid}>
                                <div className={`${styles.psDetailCell} ${styles.psDetailCellWide}`}>
                                    <span className={styles.psDetailLabel}>Accommodation</span>
                                    <span className={styles.psDetailValue}>
                                        {booking.room?.roomType?.name || booking.room?.title || 'Room'}
                                    </span>
                                </div>
                                <div className={styles.psDetailCell}>
                                    <span className={styles.psDetailLabel}>Nights</span>
                                    <span className={styles.psDetailValue}>{nights}</span>
                                </div>
                                <div className={styles.psDetailCell}>
                                    <span className={styles.psDetailLabel}>Check-in</span>
                                    <span className={styles.psDetailValue}>{fmt(booking.start_date)}</span>
                                </div>
                                <div className={styles.psDetailCell}>
                                    <span className={styles.psDetailLabel}>Check-out</span>
                                    <span className={styles.psDetailValue}>{fmt(booking.end_date)}</span>
                                </div>
                            </div>

                            {/* Includes tags */}
                            {(booking.includes_breakfast || booking.includes_pickup) && (
                                <div className={styles.psTags}>
                                    {booking.includes_breakfast && (
                                        <span className={styles.psTag}>Breakfast ×{booking.breakfast_count}</span>
                                    )}
                                    {booking.includes_pickup && (
                                        <span className={styles.psTag}>Airport Pickup</span>
                                    )}
                                </div>
                            )}
                        </>
                    )}

                </div>

                {/* ── Total band ── */}
                {booking && (
                    <div className={styles.psTotalBand}>
                        <span className={styles.psTotalLabel}>Total Amount Paid</span>
                        <span className={styles.psTotalValue}>₦{booking.total_amount?.toLocaleString()}</span>
                    </div>
                )}

                {/* ── Actions ── */}
                <div className={styles.psActions}>
                    <button onClick={() => navigate('/')} className={styles.psBtnOutline}>
                        Back to Home
                    </button>
                    {isGuest ? (
                        <button onClick={() => navigate('/login')} className={styles.psBtnPrimary}>
                            Create Account
                        </button>
                    ) : (
                        <button onClick={() => navigate('/ProfilePage')} className={styles.psBtnPrimary}>
                            View My Bookings
                        </button>
                    )}
                </div>

                {/* ── Footer note ── */}
                <div className={styles.psFooter}>
                    <p className={styles.psFooterHeadline}>Thank you for choosing Aurelia.</p>
                    <p className={styles.psFooterSub}>For any questions, email <a href="mailto:sales@aurelia-apartments.com">sales@aurelia-apartments.com</a> or call +2349152304757</p>
                </div>

            </div>
        </div>
    );
};

export default PaymentSuccess;
